// Colour, as numbers.
//
// A hex string would need its own control, could not be tweened, and could not
// be bound to the pointer. Hue, saturation and lightness are three ordinary
// sliders, so they inherit everything the schema already gives a number:
// the panel renders them, minimise strips them, the tween blends them and a
// modulation source can drive them.
//
// Saturation is also the switch. At 0 the piece defers to the theme — the
// paper and ink the page is already using, light or dark — so every piece
// written before colour existed renders exactly as it did, and a piece can
// stay theme-following on purpose. Above 0 it states its own colour.
//
// The comma form of hsl() rather than the space-separated one: canvas
// fillStyle takes any CSS colour, and this one has no version to worry about.
const css = (h, s, l) => `hsl(${Math.round(h)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`

export function resolveInk(ink, themeInk) {
  return ink.saturation > 0 ? css(ink.hue, ink.saturation, ink.lightness) : themeInk
}

export function resolveGround(frame, themeGround) {
  return frame.groundSaturation > 0
    ? css(frame.groundHue, frame.groundSaturation, frame.groundLightness)
    : themeGround
}

// ── hex, for the picker ─────────────────────────────────────────────────────
// A native colour input speaks hex, and the piece speaks three numbers. These
// two convert between them so the picker is a *view* onto hue, saturation and
// lightness rather than a replacement for them — everything downstream (the
// morph, a pointer binding, minimise) keeps working on the numbers.

export function hslToHex(h, s, l) {
  const a = s * Math.min(l, 1 - l)
  const part = (n) => {
    const k = (n + h / 30) % 12
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
    return Math.round(255 * v).toString(16).padStart(2, '0')
  }
  return `#${part(0)}${part(8)}${part(4)}`
}

export function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min
  let h = 0
  if (d) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h = h * 60
    if (h < 0) h += 360
  }
  const l = (max + min) / 2
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))
  // Rounded to the sliders' own step. A raw conversion gives values like
  // 0.45098039215686275, which the panel cannot show and a link should not
  // carry — the extra digits are below the 8-bit colour they came from.
  const q = (v) => Math.round(v * 100) / 100
  return { hue: Math.round(h), saturation: q(s), lightness: q(l) }
}

// Picking a colour always means "this piece states its own". A pure grey would
// otherwise convert to saturation 0, which is the signal for "follow the
// theme" — so the floor keeps an explicit choice explicit. Two percent is
// below what anyone can see, and the corpus greys are warm anyway.
export const MIN_STATED_SATURATION = 0.02
